import React from "react"
import {
    Grid,
    GridCell,
    TextInput,
    css,
    Theme,
    Message,
    Icon,
} from "@bdfx/mergado-ui-kit"

import trans from "trans";
import Shape from "shape-entities/types/shape";
import { getMergadoElements } from 'app/helpers/api';
import {
    updateInputValue as change,
} from "app/reducers/form";
import { AxiosResponse } from 'axios';
import { MergadoElementsType } from "products/types";
import { FormNameType, Project } from "app/types/model-types";

interface Props {
    project: Project
    shape: Shape
    formName: FormNameType
    subFormName: string
    change: typeof change
}

type AutocompleteItem = {
    text: string
    value: string
}

interface State {
    value: string
    cursorStart: number
    cursorEnd: number
    showAutocomplete: boolean
    autocompleteValues: AutocompleteItem[]
    closest: {
        word: string
        right: number
        left: number
    }
    elements: MergadoElementsType
    isLoaded: boolean
}

class TextWithElementsForm extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props)

        this.state = {
            value: '',
            cursorStart: 0,
            cursorEnd: 0,
            showAutocomplete: false,
            autocompleteValues: [],
            elements: [],
            isLoaded: false,
            closest: null,
        }
    }

    componentDidMount() {
        getMergadoElements(true, true)
            .then((response: AxiosResponse<string[]>) => {
                this.setState({
                    elements: response.data
                })
            })
            .catch(error => {
                this.setState({ isLoaded: false })
                console.debug('error while loading elements', error)
            })
            .finally(() => this.setState({ isLoaded: true }))
    }

    setValue = (v) => {
        const { subFormName, formName } = this.props

        const selectedString = v[0] === " " ? v.substring(1) : v
        const closest = this.closestWord(selectedString, this.state.cursorStart)

        this.filterAutocompleteValues(closest.word)

        this.props.change(formName, subFormName, selectedString)

        this.setState({ showAutocomplete: true, closest: closest })
    }

    filterAutocompleteValues = (keyword) => {
        const filtered = this.state.isLoaded ? this.state.elements.map((o) => {
            return { text: `${o}`, value: `%${o}%` };
        }).filter(o => (keyword && (o.value.indexOf(keyword) > -1 || o.text.toLowerCase().indexOf(keyword) > -1))) : []

        this.setState({ autocompleteValues: filtered })
    }

    selectWord = (e, textContent) => {
        const selectionStart = e.target.selectionStart
        const closest = this.closestWord(textContent, selectionStart)

        if (closest.left !== closest.right && closest.left >= 0) {
            const selectedString = e.target.value.substring(closest.left, closest.right)

            if (selectedString.includes('%')) {
                e.target.setSelectionRange(closest.left, closest.right)

                this.filterAutocompleteValues(selectedString)
                this.setState({ value: selectedString, showAutocomplete: true, closest: closest })
            }
        }
    }

    closestWord = (s: string, p: number) => {
        const str = String(s);
        const pos = Number(p) >>> 0;

        // Search for the word's beginning and end.
        const left = str.slice(0, pos + 1).search(/\S+$/)
        const right = str.slice(pos).search(/\s/);

        // The last word in the string is a special case.
        if (right < 0) {
            return { word: str.slice(left), left, right: str.length + left };
        }

        // Return the word, using the located bounds to extract it from the string.
        return { word: str.slice(left, right + pos), left, right: right + pos };
    }

    onCursorSelect = (e) => {
        this.setState({
            cursorStart: e.target.selectionStart,
            cursorEnd: e.target.selectionEnd,
            value: e.target.value.substring(e.target.selectionStart, e.target.selectionEnd),
        })
    }

    handleClickItem = (item) => {
        const { formName, subFormName, shape } = this.props
        const { cursorStart, cursorEnd } = this.state

        const newValue = shape.text_content.substring(0, cursorStart - this.state.closest.word.length) + item + shape.text_content.substring(cursorEnd, shape.text_content.length)

        this.props.change(formName, subFormName, newValue)

        this.setState({ showAutocomplete: false })
    }

    renderAutocomplete = () => {
        const { autocompleteValues } = this.state

        const rendered = autocompleteValues.map(item => {
            return <MenuItem
                key={`${item.value}-${item.text}`}
                className={'muk-autocomplete__item'}
                onClick={() => this.handleClickItem(item.value)}>
                {item.text}
            </MenuItem>
        })

        return <CustomAutocomplete>{rendered}</CustomAutocomplete>
    }

    render() {
        const { shape, project } = this.props
        const { showAutocomplete } = this.state

        //TODO: isMatch pro mergado_xml_equivalent!!!!
        //podle mapy formatu???

        let isMatch = true
        if (shape.text_content) {
            const matches = shape.text_content.match(/%([\w-:|.]+)%/g)
            if (matches) {
                matches.forEach((element) => {
                    const trimmed = element.substring(1, element.length - 1)
                    const trimmedEscaped = trimmed.replace('|', '\\|')
                    if (project.mergado_element_regex && trimmedEscaped) {
                        const regex = new RegExp(project.mergado_element_regex, "g")
                        isMatch = regex.test(trimmedEscaped)
                    }
                });
            }
        }

        return (
            <Grid cols={"auto auto auto"} gap={'5px'} style={{ marginBottom: '20px' }}>
                <GridCell>{trans('text')}</GridCell>
                <GridCell col={'span 2'}>
                    <TextInput
                        className={'exclude-click'}
                        placeholder={trans('text')}
                        onChange={this.setValue}
                        onSelect={this.onCursorSelect}
                        onMouseUp={(e) => this.selectWord(e, shape.text_content)}
                        value={shape.text_content || ''}
                    />

                    {showAutocomplete &&
                        this.renderAutocomplete()
                    }

                    {!isMatch &&
                        <Message
                            type={'error'}
                            icon={<Icon type={'exclamation-triangle'} color={Theme.red} size={28} />}
                            text={trans('no_match_with_element_regexp') + ': ' + project.mergado_element_regex}
                        />
                    }
                </GridCell>
            </Grid>
        )
    }
}


const CustomAutocomplete = css("div")({
    overflowY: 'auto',
    maxHeight: '200px',
    padding: 0,
    margin: 0,
    background: Theme.blue + '44',
})


const MenuItem = css("div")({
    padding: "5px 20px",
    fontSize: "0.9em",
    cursor: "pointer",
    borderRadius: 0,
    borderBottom: "1px solid #eee",
}, (props: any) => {
    if (props.disabled) {
        return {
            background: "#eee",
            color: "#888",
        }
    }
    if (props.selected) {
        return {
            background: Theme.blue,
            color: "white",
        }
    } else
        if (props.highlighted) {
            return {
                background: "#f4f4f4",
            }
        } else {
            return {
                background: "transparent",
            }
        }
})


export default TextWithElementsForm
